<template>
    <div class="tw:flex tw:flex-col tw:gap-6">

        <!-- Error al guardar -->
        <div
            v-if="error"
            class="tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-800 tw:px-4 tw:py-3 tw:rounded-lg tw:text-sm"
        >
            {{ error }}
        </div>

        <!-- Aviso informativo: el evento requiere pago -->
        <div
            v-if="pagoRequerido"
            class="tw:bg-blue-50 tw:border tw:border-blue-200 tw:text-blue-800 tw:px-4 tw:py-4 tw:rounded-lg tw:flex tw:items-start tw:gap-3"
        >
            <i class="material-icons tw:text-blue-500 tw:text-lg tw:mt-0.5">info</i>
            <div>
                <p class="tw:font-bold tw:m-0 tw:mb-1 tw:text-sm">{{ $t('perfil_invitaciones.total_pagar') }}</p>
                <p class="tw:text-sm tw:m-0">{{ $t('perfil_invitaciones.pago_requerido_msg') }}</p>
            </div>
        </div>

        <ValidationObserver ref="observer" tag="div" class="tw:flex tw:flex-col tw:gap-6">
            <!-- ── Datos del comprador principal ── -->
            <CardFront :titleNotBorder="$t('perfil_invitaciones.datos_comprador')" class="tw:gap-4">
                <!-- Nombre / Apellidos -->
                <div class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-4">
                    <InputText
                        :value="form.nombre"
                        :label="$t('perfil_invitaciones.nombre')"
                        :placeholder="$t('perfil_invitaciones.nombre')"
                        rules="required"
                        :name="$t('perfil_invitaciones.nombre')"
                        @input="$emit('update:form', { ...form, nombre: $event })"
                    />
                    <InputText
                        :value="form.apellidos"
                        :label="$t('perfil_invitaciones.apellidos')"
                        :placeholder="$t('perfil_invitaciones.apellidos')"
                        rules="required"
                        :name="$t('perfil_invitaciones.apellidos')"
                        @input="$emit('update:form', { ...form, apellidos: $event })"
                    />
                </div>

                <!-- Tipo documento / Número documento -->
                <div class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-4">
                    <DocumentTypePicker
                        :value="form.tipoDocumento"
                        :options="tiposDocumento"
                        :label="$t('perfil_invitaciones.tipo_documento')"
                        rules="required"
                        :name="$t('perfil_invitaciones.tipo_documento')"
                        @input="onTipoDocumentoChange"
                    />
                    <DocumentInput
                        :value="form.dni"
                        :document-type="form.tipoDocumento"
                        :label="$t('perfil_invitaciones.documento')"
                        :rules="dniRules"
                        :name="$t('perfil_invitaciones.documento')"
                        @input="$emit('update:form', { ...form, dni: $event })"
                    />
                </div>

                <!-- Email / Confirmar email -->
                <div class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-4">
                    <BaseInputEmail
                        :value="form.email"
                        :label="$t('perfil_invitaciones.email')"
                        :placeholder="$t('perfil_invitaciones.email')"
                        rules="required|email"
                        :name="$t('perfil_invitaciones.email')"
                        @input="$emit('update:form', { ...form, email: $event })"
                    />
                    <BaseInputEmail
                        :value="form.emailConfirmacion"
                        :label="$t('perfil_invitaciones.confirmar_email')"
                        :placeholder="$t('perfil_invitaciones.confirmar_email')"
                        rules="required"
                        :name="$t('perfil_invitaciones.confirmar_email')"
                        :api-validation-error="emailMismatchError"
                        @input="$emit('update:form', { ...form, emailConfirmacion: $event })"
                    />
                </div>

                <!-- Fecha de nacimiento / CP -->
                <div class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-4">
                    <BaseDateInput
                        :value="form.fecha_nacimiento"
                        :label="$t('perfil_invitaciones.fecha_nacimiento')"
                        rules="required|valid_date:noFuture"
                        :name="$t('perfil_invitaciones.fecha_nacimiento')"
                        min="1900-01-01"
                        :max="todayStr"
                        @input="$emit('update:form', { ...form, fecha_nacimiento: $event })"
                    />
                    <InputText
                        :value="form.cp"
                        :label="$t('perfil_invitaciones.cp')"
                        :placeholder="$t('perfil_invitaciones.cp')"
                        rules="required"
                        :name="$t('perfil_invitaciones.cp')"
                        @input="$emit('update:form', { ...form, cp: $event })"
                    />
                </div>

                <!-- Teléfono -->
                <div class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-4">
                    <BaseInputPhone
                        :value="form.telefono"
                        :label="$t('perfil_invitaciones.telefono')"
                        :placeholder="$t('perfil_invitaciones.telefono')"
                        :required="true"
                        :force-validate="forceValidate"
                        :name="$t('perfil_invitaciones.telefono')"
                        @input="$emit('update:form', { ...form, telefono: $event })"
                    />
                </div>
            </CardFront>

            <!-- ── Campos custom del evento (evento.fields) ── -->
            <CardFront
                v-if="camposCustom.length"
                :titleNotBorder="$t('perfil_invitaciones.datos_adicionales')"
            >
                <div
                    v-for="field in camposCustom"
                    :key="field.key"
                >
                    <InputText
                        :value="formCustom[field.key]"
                        :label="field.label || field.key"
                        :placeholder="field.placeholder || field.label || field.key"
                        :rules="field.required ? 'required' : ''"
                        :name="field.label || field.key"
                        @input="$emit('update:form-custom', { ...formCustom, [field.key]: $event })"
                    />
                </div>
            </CardFront>

            <!-- ── Datos nominativos por butaca (evento.solicitar_datos) ── -->
            <template v-if="datosPorButaca.length">

                <CardFront
                    v-for="(datos, idx) in datosPorButaca"
                    :key="idx"
                    :titleNotBorder="$t('perfil_invitaciones.datos_entrada', { n: idx + 1 })"
                    class="tw:gap-4"
                >
                    <div class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-4">
                        <InputText
                            :value="datos.nombre"
                            :label="$t('perfil_invitaciones.nombre')"
                            :placeholder="$t('perfil_invitaciones.nombre')"
                            rules="required"
                            :name="$t('perfil_invitaciones.nombre') + ' ' + (idx + 1)"
                            @input="$emit('update:datos-por-butaca', updateDatoButaca(idx, 'nombre', $event))"
                        />
                        <InputText
                            :value="datos.apellidos"
                            :label="$t('perfil_invitaciones.apellidos')"
                            :placeholder="$t('perfil_invitaciones.apellidos')"
                            rules="required"
                            :name="$t('perfil_invitaciones.apellidos') + ' ' + (idx + 1)"
                            @input="$emit('update:datos-por-butaca', updateDatoButaca(idx, 'apellidos', $event))"
                        />
                    </div>
                    <BaseInputPhone
                        v-if="teatroHash !== '14wAm2L1Jxx5B3J3xBkH'"
                        :value="datos.telefono"
                        :label="$t('perfil_invitaciones.telefono')"
                        :placeholder="$t('perfil_invitaciones.telefono')"
                        class="tw:sm:w-1/2 tw:sm:pr-2"
                        :required="true"
                        :force-validate="forceValidate"
                        @input="$emit('update:datos-por-butaca', updateDatoButaca(idx, 'telefono', $event))"
                    />
                </CardFront>
            </template>
        </ValidationObserver>
    </div>
</template>

<script setup>
    import { computed, ref } from 'vue';
    import { t } from '@/utils/i18n-helper';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import BaseDateInput from '@/modules/front/ui/BaseDateInput.vue';
    import InputText from '@/modules/front/ui/InputText.vue';
    import BaseInputEmail from '@/modules/front/ui/BaseInputEmail.vue';
    import BaseInputPhone from '@/modules/front/ui/BaseInputPhone.vue';
    import DocumentTypePicker from '@/modules/front/ui/DocumentTypePicker.vue';
    import DocumentInput from '@/modules/front/ui/DocumentInput.vue';

    const props = defineProps({
        form: {
            type: Object,
            required: true,
        },
        formCustom: {
            type: Object,
            default: () => ({}),
        },
        datosPorButaca: {
            type: Array,
            default: () => [],
        },
        camposCustom: {
            type: Array,
            default: () => [],
        },
        error: {
            type: [String, Boolean],
            default: null,
        },
        pagoRequerido: {
            type: Boolean,
            default: false,
        },
        /** Hash del teatro activo. Si es '14wAm2L1Jxx5B3J3xBkH' se oculta el campo teléfono por butaca. */
        teatroHash: {
            type: String,
            default: null,
        },
    });

    const emit = defineEmits([
        'update:form',
        'update:form-custom',
        'update:datos-por-butaca',
    ]);

    /** Opciones de tipo de documento — espejo del legacy DocumentoTipo */
    const tiposDocumento = [
        { value: 'DNI',       label: 'DNI' },
        { value: 'NIE',       label: 'NIE' },
        { value: 'PASAPORTE', label: 'Pasaporte' },
        { value: 'NIF',       label: 'NIF' },
    ];

    /**
     * Al cambiar tipo de documento se resetea el número (mismo comportamiento que el legacy).
     */
    const onTipoDocumentoChange = nuevoTipo => {
        emit('update:form', { ...props.form, tipoDocumento: nuevoTipo, dni: '' });
    };

    /** Muestra error si los emails no coinciden y ambos tienen valor */
    const emailMismatchError = computed(() => {
        const { email, emailConfirmacion } = props.form;
        if (!email || !emailConfirmacion) return null;
        if (email.trim().toLowerCase() !== emailConfirmacion.trim().toLowerCase()) {
            return t('perfil_invitaciones.email_no_coincide', {}, 'Los emails no coinciden');
        }
        return null;
    });

    /**
     * Reglas de validación reactivas para el DocumentInput.
     * Usa la regla custom `identificacion` de vee-validate que valida
     * DNI (8 dígitos + letra con control) y NIE (X/Y/Z + 7 dígitos + letra).
     * Para PASAPORTE y NIF usa la regla genérica 'otros' (alfanumérico 2-20 chars).
     */
    const dniRules = computed(() => {
        const tipo = (props.form.tipoDocumento || '').toUpperCase();
        // Mapear tipos del dropdown a los que espera la regla `identificacion`
        const mapped = (tipo === 'DNI' || tipo === 'NIE') ? tipo : 'OTROS';
        return `required|identificacion:${mapped}`;
    });

    /** Fecha de hoy en formato YYYY-MM-DD para el max del date input */
    const todayStr = new Date().toISOString().split('T')[0];

    /** Genera un nuevo array de datosPorButaca con el campo actualizado en el índice dado */
    const updateDatoButaca = (idx, campo, valor) => {
        return props.datosPorButaca.map((item, i) =>
            i === idx ? { ...item, [campo]: valor } : item
        );
    };

    // ─── Validación on-submit ────────────────────────────────────────────────
    const observer = ref(null);
    const forceValidate = ref(false);

    /**
     * Valida todos los campos del formulario.
     * Llamada por el padre (GenerarInvitacionesView) al pulsar "Siguiente".
     * @returns {Promise<boolean>} true si todos los campos son válidos
     */
    const validate = async() => {
        forceValidate.value = true;
        const result = await observer.value?.validate();
        return result?.valid ?? false;
    };

    defineExpose({ validate });
</script>
